// Evolución año a año del histórico y la cartera de reservas futuras.

import { useMemo, useState } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import type { HistoricoReservasResult } from '@/lib/types';
import { classNames } from '@/lib/utils';
import { Tarjeta, Conmutador, fmtMoney, fmtNum, fmtPct, useTemaGraficas } from './comun';

type Metrica = 'reservas' | 'noches' | 'facturacion' | 'adr';

const METRICAS: { valor: Metrica; label: string }[] = [
  { valor: 'reservas', label: 'Reservas' },
  { valor: 'noches', label: 'Noches' },
  { valor: 'facturacion', label: 'Facturación' },
  { valor: 'adr', label: 'ADR' },
];

const ES_DINERO: Record<Metrica, boolean> = { reservas: false, noches: false, facturacion: true, adr: true };

function Delta({ actual, anterior }: { actual: number; anterior: number | undefined }) {
  if (anterior == null || anterior === 0) return null;
  const d = ((actual - anterior) / anterior) * 100;
  return (
    <span className={classNames('ml-1 text-[11px] tabular-nums', d >= 0 ? 'text-emerald-600' : 'text-rose-600')}>
      {d >= 0 ? '+' : ''}
      {fmtNum(d)}%
    </span>
  );
}

export default function SeccionEvolucion({ data }: { data: HistoricoReservasResult }) {
  const tema = useTemaGraficas();
  const [metrica, setMetrica] = useState<Metrica>('reservas');
  const anioHoy = Number(data.rango.hoy.substring(0, 4));

  const grafica = useMemo(
    () => data.porAnio.map((a) => ({ anio: String(a.anio), valor: Math.round(a[metrica]) })),
    [data, metrica],
  );
  const dinero = ES_DINERO[metrica];

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Tarjeta
          titulo="Evolución por año"
          info="Reservas confirmadas agrupadas por el año de su fecha de entrada. Las noches y la facturación (alojamiento + limpieza + extras, IVA incluido) van enteras al año de la entrada, sin prorratear."
          className="lg:col-span-2"
          extra={<Conmutador opciones={METRICAS} valor={metrica} onChange={setMetrica} />}
        >
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={grafica} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={tema.grid} />
              <XAxis dataKey="anio" tick={{ fontSize: 12, fill: tema.axis }} />
              <YAxis tick={{ fontSize: 12, fill: tema.axis }} tickFormatter={(v) => (dinero && v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v))} />
              <Tooltip
                formatter={(v: any) => [dinero ? fmtMoney(Number(v)) : fmtNum(Number(v)), METRICAS.find((m) => m.valor === metrica)?.label]}
                cursor={{ fill: tema.cursor }}
                contentStyle={tema.tooltip}
              />
              <Bar dataKey="valor" fill={tema.bar} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Tarjeta>

        <Tarjeta
          titulo="Cartera futura"
          info="Reservas confirmadas cuya entrada es posterior a hoy: lo que ya está vendido para los próximos meses. Cuentan también en el resto de la pantalla."
        >
          <div className="grid grid-cols-2 gap-4">
            <Dato label="Reservas" valor={fmtNum(data.cartera.reservas)} />
            <Dato label="Noches" valor={fmtNum(data.cartera.noches)} />
            <Dato label="Huéspedes" valor={fmtNum(data.cartera.huespedes)} />
            <Dato label="Facturación" valor={fmtMoney(data.cartera.facturacion)} />
          </div>
          <p className="mt-4 text-xs text-sereno-dark/40">
            Última entrada confirmada: {data.rango.ultimaEntrada ? data.rango.ultimaEntrada.split('-').reverse().join('/') : '—'}
          </p>
        </Tarjeta>
      </div>

      <Tarjeta
        titulo="Detalle por año"
        info="Cancelación = canceladas / (confirmadas + canceladas) del año de entrada. Repetidoras = reservas de clientes que ya habían venido un año anterior. Listings = anuncios de Hostify con alguna reserva confirmada ese año (un alojamiento del CRM puede tener varios, y los que no están dados de alta cuentan sueltos). Los deltas comparan con el año anterior; los del año en curso van contra el anterior completo. Ocupación aproximada = noches vendidas ya pasadas / noches en las que cada alojamiento estaba en cartera (desde su primera entrada hasta su última salida, o hasta hoy si sigue activo). No es la ocupación oficial: no sabe cuándo se bloqueó un piso."
        className="mb-6"
      >
        <div className="overflow-x-auto -mx-6 px-6">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="text-left text-xs text-sereno-dark/50 border-b border-sereno-dark/10">
                <th className="py-2 font-medium">Año</th>
                <th className="py-2 font-medium text-right">Reservas</th>
                <th className="py-2 font-medium text-right">Noches</th>
                <th className="py-2 font-medium text-right">Huéspedes</th>
                <th className="py-2 font-medium text-right">Facturación</th>
                <th className="py-2 font-medium text-right">Neto</th>
                <th className="py-2 font-medium text-right">ADR</th>
                <th className="py-2 font-medium text-right">Estancia</th>
                <th className="py-2 font-medium text-right">Antelación</th>
                <th className="py-2 font-medium text-right">Cancelación</th>
                <th className="py-2 font-medium text-right">Repetidoras</th>
                <th className="py-2 font-medium text-right">Listings</th>
                <th className="py-2 font-medium text-right">Ocupación aprox.</th>
              </tr>
            </thead>
            <tbody>
              {data.porAnio.map((a, i) => {
                const prev = data.porAnio[i - 1];
                const futuro = a.anio > anioHoy;
                return (
                  <tr key={a.anio} className={classNames('border-b border-sereno-dark/5 last:border-0', futuro && 'text-sereno-dark/50 italic')}>
                    <td className="py-2 font-semibold">
                      {a.anio}
                      {a.anio === anioHoy && <span className="ml-1.5 badge bg-sereno-blue/40 text-sereno-dark not-italic">en curso</span>}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {fmtNum(a.reservas)}
                      {!futuro && <Delta actual={a.reservas} anterior={prev?.reservas} />}
                    </td>
                    <td className="py-2 text-right tabular-nums">{fmtNum(a.noches)}</td>
                    <td className="py-2 text-right tabular-nums">{fmtNum(a.huespedes)}</td>
                    <td className="py-2 text-right tabular-nums font-semibold">
                      {fmtMoney(a.facturacion)}
                      {!futuro && <Delta actual={a.facturacion} anterior={prev?.facturacion} />}
                    </td>
                    <td className="py-2 text-right tabular-nums">{fmtMoney(a.neto)}</td>
                    <td className="py-2 text-right tabular-nums">{fmtMoney(a.adr)}</td>
                    <td className="py-2 text-right tabular-nums">{fmtNum(a.estanciaMedia, 1)} n.</td>
                    <td className="py-2 text-right tabular-nums">{fmtNum(a.antelacionMedia)} d.</td>
                    <td className="py-2 text-right tabular-nums">
                      {fmtPct(a.tasaCancelacion)} <span className="text-xs text-sereno-dark/40">({fmtNum(a.canceladas)})</span>
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {a.reservas > 0 ? fmtPct((a.repetidoras / a.reservas) * 100) : '—'}{' '}
                      <span className="text-xs text-sereno-dark/40">({fmtNum(a.repetidoras)})</span>
                    </td>
                    <td className="py-2 text-right tabular-nums">{fmtNum(a.propiedades)}</td>
                    <td className="py-2 text-right tabular-nums">{a.ocupacion == null ? '—' : fmtPct(a.ocupacion)}</td>
                  </tr>
                );
              })}
              {data.porAnio.length === 0 && (
                <tr>
                  <td colSpan={13} className="py-4 text-center text-sereno-dark/40">Sin datos</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Tarjeta>
    </>
  );
}

function Dato({ label, valor }: { label: string; valor: string }) {
  return (
    <div>
      <p className="text-xs text-sereno-dark/50 mb-1">{label}</p>
      <p className="text-lg font-bold text-sereno-dark">{valor}</p>
    </div>
  );
}
